import type { FC } from "react";
import { useState, useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { tableActionsCreator } from "app/actions/tableActions";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { translate } from "app/filters/i18nFilter";
import type { EKWGeneratorTableTabs } from "app/common/keyword-research/keyword-generator/types";

import type { ApiParams } from "app/common/keyword-research/keywords-overview/keywordsOverviewPageTypes";

import { getKeywordIdeasTableCoulmns } from "./keywordIdeasColumnsConfig";
import { ENDPOINT, ROW_COUNT, TABLE_SELECTION_KEY, TABLE_SELECTION_PROPERTY } from "./keywordIdeasConsts";
import { KeywordIdeasFooter } from "./KeywordIdeasFooter";
import type { ITransformedKeywordIdeasData } from "./keywordIdeasTypes";
import { getApiParams, transformData } from "./keywordIdeasUtils";

interface IKeywordIdeasTable {
    hasChange: boolean;
    tabName: EKWGeneratorTableTabs;
}

export const KeywordIdeasTable: FC<IKeywordIdeasTable> = ({ hasChange, tabName }) => {
    const dispatch = useDispatch();
    const { params } = useCurrentStateAndParams();
    const { keyword, duration, country, webSource, tab: selectedTab } = params;
    const { selectRows } = tableActionsCreator(TABLE_SELECTION_KEY, TABLE_SELECTION_PROPERTY);
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);
    const keys = keywordGroupsTransforms.getGroupIdFromUrlParam(keyword);

    const [initialFilters, setInitialFilters] = useState<ApiParams | null>(null);
    const [isLoadingFilters, setIsLoadingFilters] = useState(true);

    useEffect(() => {
        const loadApiParams = async () => {
            try {
                setIsLoadingFilters(true);
                const apiParams = await getApiParams(isKeywordGroup, keys, duration, country, tabName);
                setInitialFilters(apiParams);
            } catch (error) {
                Sentry.error("Error loading API parameters for keyword ideas table", error);
            } finally {
                setIsLoadingFilters(false);
            }
        };

        void loadApiParams();
    }, [isKeywordGroup, keys, duration, country, tabName]);

    const columns = getKeywordIdeasTableCoulmns(country, duration, hasChange, isKeywordGroup, selectedTab);

    const getDataCallback = (data: ITransformedKeywordIdeasData) => {
        if (tabName === selectedTab) {
            dispatch(selectRows(data.records));
        }
    };

    const onDataError = (...args) => {
        Sentry.error(`Error fetching keywords ideas table data for keyword analysis overview`, args?.[0]);
    };

    if (isLoadingFilters || !initialFilters) {
        return null;
    }

    return (
        <SWReactTableWrapperWithSelection
            tableColumns={columns}
            rowsPerPage={ROW_COUNT}
            tableSelectionKey={TABLE_SELECTION_KEY}
            tableSelectionProperty={TABLE_SELECTION_PROPERTY}
            serverApi={ENDPOINT}
            initialFilters={initialFilters}
            requestBody={[]}
            transformData={transformData(duration, country, webSource, hasChange)}
            getDataCallback={getDataCallback}
            onDataError={onDataError}
            paginationProps={{ showPagination: false }}
            numRowsForLoader={ROW_COUNT + 1}
            tableOptions={{
                noDataTitle: translate("common.noresults"),
                noDataSubtitle: translate("keyword.analysis.overview.page.v2.trend_over_time.noData.subtitle"),
                tableSelectionTrackingParam: "keyword",
            }}
            tableHeight={232}
            footerComponent={<KeywordIdeasFooter />}
        />
    );
};
